import useAddPostForm from '@buildwise/pages/addPost/hooks/useAddPostForm';
import {
  ArticleFieldsType,
  ArticleSourceType,
  ArticleType,
} from '@buildwise/pages/knowledge/hooks/useArticlesQuery';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import React, { useMemo, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { useHelpCenterArticlesQuery } from '../queries/useHelpCenterArticlesQuery';
import PostDetailsForm from 'components/modals/addPostModal/PostDetailsForm';
import { CONFIGS } from '@buildwise/libs/configurations';
import CustomDialogTitle from 'components/modal/CustomDialogTitle';

type AddHelpCenterArticleCollectionModalPros = {
  article?: ArticleType;
  openArticleCollectionModal: boolean;
  openAddArticlesModal?: boolean;
  setOpenArticleCollectionModal: React.Dispatch<React.SetStateAction<boolean>>;
  setOpenAddArticlesModal?: React.Dispatch<React.SetStateAction<boolean>>;
};

function AddHelpCenterArticleCollectionModal({
  article,
  openArticleCollectionModal,
  openAddArticlesModal,
  setOpenArticleCollectionModal,
  setOpenAddArticlesModal,
}: AddHelpCenterArticleCollectionModalPros) {
  const { updateHelpCenterArticlesCache } = useHelpCenterArticlesQuery();
  const handleClose = () => {
    setOpenArticleCollectionModal(false);
    resetFormWithState();
  };
  const [articleInfo, setArticleInfo] = useState(article);

  const isEditing = useMemo(() => !!articleInfo, [articleInfo]);
  const {
    articleFields,
    postForm,
    isSubmitting,
    onSubmit,
    resetFormWithState,
    loadingFiles,
    onDrop,
  } = useAddPostForm({
    isEditing,
    data: articleInfo,
    createPlaylist: !isEditing,
    setOpen: () => {
      if (isEditing) {
        handleClose();
      }
    },
    articleType: ArticleSourceType.HELP_CENTER,
    articleFieldsType: ArticleFieldsType.HELP_CENTER,
    onAddArticleCallback: ({ article }) => {
      updateHelpCenterArticlesCache({ action: 'update', updatedArticle: article });
      if (!isEditing) {
        setArticleInfo(article);
      }
    },
    updateArticleCallback: ({ updatedArticle }) => {
      updateHelpCenterArticlesCache({ action: 'update', updatedArticle });
    },
  });

  return (
    <>
      <Dialog
        open={openArticleCollectionModal}
        fullWidth
        maxWidth="sm"
        sx={{ '& .MuiPaper-root': { maxHeight: '90%', minHeight: 230 } }}
      >
        <CustomDialogTitle onClose={handleClose}>
          <Title>{isEditing ? 'Edit' : 'New'} Help Article Collection</Title>
        </CustomDialogTitle>
        <DialogContent
          dividers
          sx={{
            borderBottom: 'none',
          }}
        >
          <FormProvider {...postForm}>
            <PostDetailsForm
              article={articleInfo}
              articleType={ArticleSourceType.HELP_CENTER}
              isEditing={isEditing}
              isFullScreen
              loadingFiles={loadingFiles}
              onDrop={onDrop}
              articleFields={articleFields}
              configName={CONFIGS.HELP_CENTER_INPUT_FIELDS.configName}
              getFieldWidth={() => '100%'}
              isCreatingOrEditingPlaylist
              openAddArticlesModal={openAddArticlesModal}
              setOpenAddArticlesModal={setOpenAddArticlesModal}
            />
          </FormProvider>
        </DialogContent>
        <DialogActions>
          <Button variant="outlined" onClick={handleClose} disabled={isSubmitting} sx={{ mr: 2 }}>
            Cancel
          </Button>
          <Button variant="contained" onClick={onSubmit} disabled={isSubmitting}>
            {isSubmitting ? (
              <CircularProgress sx={(theme) => ({ color: theme.palette.custom.black })} size={20} />
            ) : (
              'Save'
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

const Title = styled(Typography, { label: 'Title' })(({ theme }) => ({
  fontSize: 20,
  fontWeight: 700,
  color: theme.palette.custom.black,
}));

export default AddHelpCenterArticleCollectionModal;
